import { useLazyQuery } from '@apollo/react-hooks';
import formatString from 'format-string-by-pattern';
import gql from 'graphql-tag';
import _ from 'lodash';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import defaultMessage from '../../../context/Message';
import { VendorContext, CommonDataContext } from '../../../context';
import { getSidebarEmailFontSizeByWidth, getURLParameter } from '../../../helpers';
import fonts from './fonts.module.css';
import SidebarStyles from './Sidebar.module.css';

const config = require('../../../config');

const GET_CONTACT = gql`
  query contact($dynamicsId: ID!) {
    contact(dynamicsId: $dynamicsId) {
      email
      firstName
      lastName
      fullName
      title
      phoneNumber
      mugshot
      availability
      vendorContactRole
    }
  }
`;

const SidebarRender = (props: any) => {
  const { repData, vendorContact, publicAccount, setRepData, partnerLink } = props;
  const { color: vendorColor, panelTextColor } = useContext(VendorContext);
  const { task } = useContext(CommonDataContext);
  const locationId = getURLParameter('locationId');
  const isTaskPage = window.location.href.includes('task-page');

  const emailRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const getRepDataToSet = () => {
      const getLocationContact = () => {
        if (!locationId || _.isEmpty(publicAccount.locations)) {
          return;
        }
        const locationInfo = _.find(publicAccount.locations, { locationId });
        return _.get(locationInfo, 'ocaContact');
      };

      if (isTaskPage && task) {
        return task.createdByUserProfile || _.get(publicAccount, 'primaryContact');
      }

      return _.get(partnerLink, 'dealerContact') ||
        vendorContact ||
        getLocationContact() ||
        _.get(publicAccount, 'primaryContact');
    };

    const repDataToSet = getRepDataToSet();
    if (_.isEmpty(repDataToSet)) {
      return;
    }
    setRepData({
      officePhone: repDataToSet.phoneNumber || '',
      mugshot: repDataToSet.mugshot || '',
      email: repDataToSet.email || '',
      name: repDataToSet.fullName,
      availability: repDataToSet.availability || 'Monday-Friday, 8am-5pm PST',
    });
  }, [publicAccount, vendorContact, locationId, partnerLink]);

  useLayoutEffect(() => {
    if (emailRef.current && emailRef.current.offsetWidth) {
      emailRef.current.style.fontSize = getSidebarEmailFontSizeByWidth(emailRef.current.offsetWidth);
      emailRef.current.style.wordBreak = 'break-word';
    }
  }, [repData.email]);

  const { email, mugshot, name, fullName, officePhone, phoneNumber } = repData;

  return (
    <div
      className={SidebarStyles.root}
      style={{ backgroundColor: vendorColor }}
    >
      <div className={SidebarStyles.body}>
        <div className={SidebarStyles.mdColumn}>
          <h3 className={fonts.h3} style={{ color: panelTextColor, margin: '15px' }}>
            <FormattedMessage
              id="needHelp"
              defaultMessage={defaultMessage.needHelp}
            />
          </h3>
          <p style={{ color: panelTextColor, textAlign: 'center' }}>
            <FormattedMessage
              id="representativesHelp"
              defaultMessage={defaultMessage.representativesHelp}
            />
          </p>
        </div>
        <div className={SidebarStyles.mdColumn}>
          <img
            src={
              mugshot && mugshot.includes('/')
                ? `https://${config.S3_BUCKET_NAME}.s3-us-west-2.amazonaws.com/${mugshot}`
                : 'https://credit-app-images.s3-us-west-2.amazonaws.com/avatar-placeholder.png'
            }
            alt="agent"
            className={SidebarStyles.photo}
          />
        </div>
        <div className={SidebarStyles.mdColumn}>
          <h3 className={fonts.h5} style={{ color: panelTextColor }}>
            {name || fullName}
          </h3>
          <a
            ref={emailRef}
            href={`mailto:${email}`}
            style={{
              color: panelTextColor,
              textDecoration: 'none',
            }}
          >
            {email}
          </a>
          <p style={{ color: panelTextColor }}>
            {formatString('(999) 999-9999', officePhone || phoneNumber)}
          </p>
        </div>
      </div>
    </div>
  );
};

const Sidebar = (props: any) => {
  const { repData, setRepData, publicAccount, partnerLink } = props;
  const vendorContactId = getURLParameter('vendorcontactId');
  const [vendorContact, setVendorContact] = useState();
  const { isLenderDealerOCA } = useContext(CommonDataContext);

  const [getContact, { data: contactData }] = useLazyQuery(GET_CONTACT);

  useEffect(() => {
    //no need to set specific contact by vendorContactId for LD OCA. Contact will be taken from publicAccount.primaryContact
    if (vendorContactId && !isLenderDealerOCA) {
      getContact({
        variables: { dynamicsId: vendorContactId }
      });
    }

  }, [vendorContactId]);

  useEffect(() => {
    if (contactData) {
      setVendorContact(contactData.contact);
    }
  }, [contactData]);

  return (
    <SidebarRender
      vendorContact={vendorContact}
      publicAccount={publicAccount}
      setRepData={setRepData}
      repData={repData}
      partnerLink={partnerLink}
    />
  );
};
export default Sidebar;
